import React from 'react';
import { Grid } from '@mui/material';

import { useStylesAccordionDetails } from '../styles';
import ImagePdfPreview from 'modules/views/shared/ImagePdfPreview';

export interface IAccordionDetailsProps {
  data: Array<{
    label: string;
    value: string;
    hasSectionSeparator?: boolean;
    fullWidth?: boolean;
    imageUrls?: string[];
  }>;
  sections?: Array<{
    title: string;
    fields: Array<{
      label: string;
      value: string;
      hasSectionSeparator?: boolean;
      fullWidth?: boolean;
      imageUrls?: string[];
    }>;
    hasSectionSeparator?: boolean;
  }>;
  responsiveValues?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

const toFieldTestId = (label: string) =>
  `consent-info-field-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;

const AccordionDetails = ({ data, sections, responsiveValues }: IAccordionDetailsProps) => {
  const classes = useStylesAccordionDetails();

  const renderSectionData = (sectionData: IAccordionDetailsProps['data']) => {
    return sectionData?.map((item, index) => {
      const fieldId = toFieldTestId(item.label);
      return (
        <>
          {item.hasSectionSeparator && (
            <Grid item xs={12} className={classes.sectionSeparator}>
              <hr />
            </Grid>
          )}
          <Grid
            item
            key={index}
            xs={item.fullWidth ? 12 : responsiveValues?.xs || 12}
            sm={item.fullWidth ? 12 : responsiveValues?.sm || 6}
            md={item.fullWidth ? 12 : responsiveValues?.md || 6}
            lg={item.fullWidth ? 12 : responsiveValues?.lg || 4}
            xl={item.fullWidth ? 12 : responsiveValues?.xl || 4}
            className={classes.item}
            data-testid={fieldId}
          >
            <p className={classes.label} data-testid={`${fieldId}-label`}>
              {item.label}
            </p>
            {item.imageUrls?.length ? (
              <div className={classes.imageUrlsRow}>
                {item.imageUrls.map((url, i) => (
                  <ImagePdfPreview key={i} url={url} alt={item.label} imageClassName={classes.storedFieldImage} />
                ))}
              </div>
            ) : (
              <p className={classes.value} data-testid={`${fieldId}-value`}>
                {item.value}
              </p>
            )}
          </Grid>
        </>
      );
    });
  };

  return (
    <Grid container spacing={2} className={classes.accordionDetailsContainer}>
      {sections
        ? sections.map((section, sectionIndex) => {
            const sectionId = section.title
              ? `consent-info-section-${section.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/(^-|-$)/g, '')}`
              : undefined;
            return (
              <React.Fragment key={sectionIndex}>
                {section.fields.length > 0 && (
                  <>
                    {section.title && (
                      <Grid item xs={12} className={classes.sectionTitle} data-testid={sectionId}>
                        {section.title}
                      </Grid>
                    )}
                    {renderSectionData(section.fields)}
                    {section.hasSectionSeparator && (
                      <Grid item xs={12} className={classes.sectionSeparator}>
                        <hr />
                      </Grid>
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })
        : renderSectionData(data)}
    </Grid>
  );
};

export default AccordionDetails;
